import { useEffect, useState, useCallback } from 'react';
import DuplicatePaymentsCard from '@/components/admin/DuplicatePaymentsCard';
import BankReconciliation from '@/components/admin/BankReconciliation';
import {
  ArrowLeft, AlertTriangle, BookOpen, Download, Hammer, RefreshCw, Search, Send, SlidersHorizontal, Wallet, X,
} from 'lucide-react';
import axios from 'axios';
import LedgerStatementTable, { fmtVES, fmtDate, ENTRY_TYPES } from '@/components/LedgerStatementTable';
import { downloadAuthedFile } from '@/lib/downloadFile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/super-admin`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function AutoSwitches({ merchant, setToast, onChanged }) {
  const [confirm, setConfirm] = useState(null); // 'sweep' | 'split'
  const [busy, setBusy] = useState(false);
  const sweepOn = !merchant.settlement_paused;
  const splitOn = !merchant.commission_split_paused;

  const apply = async (which, nextOn) => {
    setBusy(true);
    try {
      const path = which === 'sweep' ? 'alliance-pause' : 'commission-split-pause';
      await axios.put(`${API}/merchants/${merchant.merchant_id}/${path}`, { paused: !nextOn }, { headers: headers() });
      setToast({
        type: 'success',
        message: `${which === 'sweep' ? 'Barrido automático' : 'Reparto de comisiones'} ${nextOn ? 'ACTIVADO' : 'APAGADO'}`,
      });
      setConfirm(null);
      onChanged?.();
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.detail || 'No se pudo cambiar' });
    } finally {
      setBusy(false);
    }
  };

  const toggle = (which, currentlyOn) => {
    if (!currentlyOn) setConfirm(which);   // prender siempre pide confirmación
    else apply(which, false);              // apagar es directo
  };

  const Row = ({ which, label, hint, on, testid }) => (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-[11px] text-gray-500 leading-tight">{hint}</p>
      </div>
      <button
        data-testid={testid}
        role="switch"
        aria-checked={on}
        disabled={busy}
        onClick={() => toggle(which, on)}
        className={`relative w-12 h-6 rounded-full shrink-0 transition-colors disabled:opacity-50 ${on ? 'bg-emerald-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${on ? 'left-6' : 'left-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div className="mt-4 pt-3 border-t" data-testid={`auto-switches-${merchant.merchant_id}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Automático</p>
      <Row
        which="sweep"
        label="Barrido automático"
        hint={`Corte diario: ${merchant.rate_pct}% a Caura + resto a la cuenta sweep`}
        on={sweepOn}
        testid={`switch-sweep-${merchant.merchant_id}`}
      />
      {merchant.commission_split_configured && (
        <Row
          which="split"
          label="Reparto de comisiones"
          hint="Divide la comisión de Caura entre los beneficiarios tras cada corte"
          on={splitOn}
          testid={`switch-split-${merchant.merchant_id}`}
        />
      )}

      {confirm && (
        <Modal
          title={confirm === 'sweep' ? 'Activar barrido automático' : 'Activar reparto de comisiones'}
          onClose={() => setConfirm(null)}
        >
          <div className="space-y-4" data-testid="auto-switch-confirm">
            <p className="text-sm text-gray-700">
              {confirm === 'sweep' ? (
                <>A partir de ahora el corte diario de <strong>{merchant.company_name || merchant.email}</strong> se
                ejecutará solo, moviendo dinero real: <strong>{merchant.rate_pct}%</strong> de comisión a Caura y el
                resto a la cuenta sweep del comercio.</>
              ) : (
                <>A partir de ahora, 10 minutos después de cada corte exitoso, la comisión que cobra Caura se
                repartirá automáticamente entre los beneficiarios configurados (CCE real).</>
              )}
            </p>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
              <p className="text-xs text-amber-900">Saldo actual del ledger</p>
              <p className="text-2xl font-bold text-amber-900" data-testid="auto-switch-confirm-balance">
                {fmtVES(merchant.balance)}
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Confirma que este saldo coincide con el disponible real del banco antes de activar.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirm(null)}
                className="flex-1 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >Cancelar</button>
              <button
                data-testid="auto-switch-confirm-btn"
                disabled={busy}
                onClick={() => apply(confirm, true)}
                className="flex-1 px-3 py-2 text-sm bg-emerald-700 text-white rounded-lg font-semibold hover:bg-emerald-800 disabled:opacity-50"
              >{busy ? 'Activando…' : 'Sí, activar'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function OrphanCreditsPanel({ setToast, onReconciled }) {
  const [credits, setCredits] = useState([]);
  const [total, setTotal] = useState(0);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orphan-credits?status_filter=pending`, { headers: headers() });
      setCredits(res.data.credits || []);
      setTotal(res.data.pending_total || 0);
    } catch (e) {
      setCredits([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (orphanId, action) => {
    setBusyId(orphanId);
    try {
      const res = await axios.post(`${API}/orphan-credits/${orphanId}/${action}`, {}, { headers: headers() });
      setToast({
        type: 'success',
        message: action === 'reconcile'
          ? `Conciliado como orden ${res.data.order_code}`
          : 'Marcado como ajeno al canal Caura',
      });
      await load();
      onReconciled?.();
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.detail || 'No se pudo procesar' });
    } finally {
      setBusyId(null);
    }
  };

  if (credits.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl p-5" data-testid="orphan-credits-panel">
      <div className="flex items-start gap-3">
        <Search className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-amber-900">
            {credits.length} pago(s) en el banco sin orden en Caura · {fmtVES(total)}
          </h3>
          <p className="text-sm text-amber-800 mt-0.5">
            Entró dinero a la cuenta del comercio pero no hay orden ni asiento. Hasta conciliarlo, el
            ledger queda por debajo del saldo real del banco.
          </p>
          <div className="mt-4 space-y-2">
            {credits.map((c) => (
              <div
                key={c.orphan_id}
                data-testid={`orphan-credit-${c.orphan_id}`}
                className="bg-white rounded-lg border border-amber-200 p-3 flex items-center gap-3 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900">
                    {fmtVES(c.mov_amount)} <span className="font-normal text-gray-500 text-sm">· ref {c.mov_reference}</span>
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {c.company_name} · {c.mov_date} {c.mov_time} · {c.mov_description || 'sin concepto'}
                  </p>
                </div>
                <button
                  data-testid={`orphan-reconcile-${c.orphan_id}`}
                  disabled={busyId === c.orphan_id}
                  onClick={() => act(c.orphan_id, 'reconcile')}
                  className="px-3 py-1.5 text-sm bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-50"
                >Conciliar al ledger</button>
                <button
                  data-testid={`orphan-ignore-${c.orphan_id}`}
                  disabled={busyId === c.orphan_id}
                  onClick={() => act(c.orphan_id, 'ignore')}
                  className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >No es de Caura</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} data-testid="ledger-modal-close"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function DisperseModal({ merchant, onClose, onDone, setToast }) {
  const rate = Number(merchant.rate_pct || 0);
  const balance = Number(merchant.balance || 0);
  const commission = Math.round(balance * rate) / 100;
  const net90 = Math.round((balance - commission) * 100) / 100;
  const [amount, setAmount] = useState(
    rate > 0 && net90 > 0 ? net90.toFixed(2) : '',
  );
  const [busy, setBusy] = useState(false);
  const willSweepAll = !amount || Math.abs(parseFloat(amount) - balance) < 0.01;
  const submit = async () => {
    setBusy(true);
    try {
      const body = amount ? { amount: parseFloat(amount) } : {};
      const res = await axios.post(`${API}/ledger/${merchant.merchant_id}/disperse`, body, { headers: headers() });
      const d = res.data;
      if (d.skipped) {
        setToast({ type: 'info', message: `Sin saldo suficiente para dispersar (saldo: ${fmtVES(d.balance)})` });
      } else {
        setToast({ type: 'success', message: `Dispersados ${fmtVES(d.dispersed)} en ${d.chunks} CCE(s)` });
      }
      onDone();
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.detail || 'Error en la dispersión' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Dispersión manual — ${merchant.email}`} onClose={onClose}>
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4" data-testid="disperse-commission-warning">
        <p className="text-xs font-bold text-amber-900 uppercase tracking-wide">La dispersión manual NO cobra la comisión de Caura</p>
        <p className="text-sm text-amber-900 mt-1">
          Si envías el 100% del saldo, Caura deja de cobrar su <strong>{rate}%</strong> y habrá que
          recuperarlo del comercio. Para cobrarla usa <strong>Alianzas → Ejecutar settlement ahora</strong>.
        </p>
        <div className="mt-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-amber-900">Saldo del ledger</span><b className="text-amber-900">{fmtVES(balance)}</b></div>
          <div className="flex justify-between"><span className="text-amber-900">Comisión Caura {rate}%</span><b className="text-amber-900" data-testid="disperse-commission-amount">{fmtVES(commission)}</b></div>
          <div className="flex justify-between border-t border-amber-300 pt-1"><span className="text-amber-900">Dispersable al comercio</span><b className="text-amber-900" data-testid="disperse-suggested-amount">{fmtVES(net90)}</b></div>
        </div>
      </div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Monto a dispersar</label>
      <input
        data-testid="disperse-amount-input"
        type="number" min="0" step="0.01" placeholder={`Monto (máx ${balance})`}
        value={amount} onChange={(e) => setAmount(e.target.value)}
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
      <div className="flex gap-2 mt-2 mb-4">
        <button
          data-testid="disperse-use-net-btn"
          onClick={() => setAmount(net90.toFixed(2))}
          className="px-2.5 py-1 text-xs border rounded-lg hover:bg-gray-50"
        >Dejar la comisión ({fmtVES(net90)})</button>
        <button
          data-testid="disperse-use-all-btn"
          onClick={() => setAmount(balance.toFixed(2))}
          className="px-2.5 py-1 text-xs border rounded-lg hover:bg-gray-50"
        >Todo el saldo ({fmtVES(balance)})</button>
      </div>
      {willSweepAll && rate > 0 && (
        <p className="text-xs text-red-700 font-semibold mb-3" data-testid="disperse-all-warning">
          ⚠️ Vas a enviar el 100%: Caura quedará sin cobrar {fmtVES(commission)}.
        </p>
      )}
      <button
        data-testid="disperse-confirm-btn"
        disabled={busy}
        onClick={submit}
        className="w-full bg-purple-700 text-white rounded-lg py-2.5 font-semibold hover:bg-purple-800 disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        <Send className="w-4 h-4" /> {busy ? 'Ejecutando CCE…' : 'Dispersar'}
      </button>
    </Modal>
  );
}

function AdjustModal({ merchant, onClose, onDone, setToast }) {
  const [direction, setDirection] = useState('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reversesBankFee, setReversesBankFee] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/ledger/${merchant.merchant_id}/adjust`,
        { direction, amount: parseFloat(amount), reason, reverses_bank_fee: direction === 'credit' && reversesBankFee },
        { headers: headers() },
      );
      setToast({ type: 'success', message: 'Ajuste registrado en el ledger' });
      onDone();
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.detail || 'Error al registrar ajuste' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Ajuste manual — ${merchant.email}`} onClose={onClose}>
      <div className="space-y-3">
        <select
          data-testid="adjust-direction-select"
          value={direction} onChange={(e) => setDirection(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        >
          <option value="credit">Crédito (+) sumar al saldo</option>
          <option value="debit">Débito (−) restar del saldo</option>
        </select>
        <input
          data-testid="adjust-amount-input"
          type="number" min="0" step="0.01" placeholder="Monto VES"
          value={amount} onChange={(e) => setAmount(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
        <textarea
          data-testid="adjust-reason-input"
          placeholder="Motivo del ajuste (obligatorio, queda en auditoría)"
          value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm" rows={3}
        />
        {direction === 'credit' && (
          <label className="flex items-start gap-2 text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <input
              data-testid="adjust-reverses-bankfee-checkbox"
              type="checkbox"
              checked={reversesBankFee}
              onChange={(e) => setReversesBankFee(e.target.checked)}
              className="mt-0.5"
            />
            <span>Este crédito <strong>revierte una comisión bancaria errónea</strong> (barrido/retiro mal capturado). El corte diario lo restará del total de fees del banco.</span>
          </label>
        )}
        <button
          data-testid="adjust-confirm-btn"
          disabled={busy || !amount || !reason.trim()}
          onClick={submit}
          className="w-full bg-blue-700 text-white rounded-lg py-2.5 font-semibold hover:bg-blue-800 disabled:opacity-50"
        >
          {busy ? 'Registrando…' : 'Registrar ajuste'}
        </button>
      </div>
    </Modal>
  );
}

function PreviewRow({ label, value, bold, highlight, negative }) {
  return (
    <div className={`flex justify-between px-3 py-2 ${highlight ? 'bg-blue-50' : ''}`}>
      <span className="text-gray-600">{label}</span>
      <span className={`${bold ? 'font-bold' : ''} ${highlight ? 'text-blue-700' : negative ? 'text-red-600' : 'text-gray-900'}`}>{value}</span>
    </div>
  );
}

// '2026-07-14T22:15:00+00:00' -> '2026-07-14T22:15' (reloj UTC para datetime-local)
function utcIsoToInput(iso) {
  return iso ? String(iso).slice(0, 16) : '';
}
// 'YYYY-MM-DDTHH:MM' interpretado como UTC -> ISO con offset +00:00
function inputToUtcIso(val) {
  return val ? `${val}:00+00:00` : null;
}

function PreviewModal({ merchant, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [wStart, setWStart] = useState('');
  const [wEnd, setWEnd] = useState('');
  const [fullBalance, setFullBalance] = useState(!!merchant.full_balance_mode);
  const [queried, setQueried] = useState({ start: null, end: null, full: false });
  const [executing, setExecuting] = useState(false);
  const [execResult, setExecResult] = useState(null);

  const fetchPreview = useCallback(async (startIso, endIso, fb = false) => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (fb) {
        params.full_balance = true;
      } else {
        if (startIso) params.window_start = startIso;
        if (endIso) params.window_end = endIso;
      }
      const res = await axios.get(`${API}/ledger/${merchant.merchant_id}/settlement-preview`, { headers: headers(), params });
      return res.data;
    } catch (e) {
      setErr(e.response?.data?.detail || 'Error al simular el corte');
      return null;
    } finally {
      setLoading(false);
    }
  }, [merchant.merchant_id]);

  // Primera carga: obtiene defaults + último corte y, si hay un corte previo
  // anterior al inicio default de 24h, re-simula "desde el último corte" (sin gaps).
  useEffect(() => {
    let alive = true;
    (async () => {
      // Comercios configurados en modo "barrer todo el saldo" (p.ej. Lotería):
      // el preview abre en ese mismo modo para que los números coincidan con
      // lo que hará el corte real (automático o manual).
      if (merchant.full_balance_mode) {
        const d = await fetchPreview(null, null, true);
        if (!alive || !d) return;
        setWStart(utcIsoToInput(d.window_start));
        setWEnd(utcIsoToInput(d.window_end));
        setData(d);
        setQueried({ start: null, end: null, full: true });
        return;
      }
      const first = await fetchPreview();
      if (!alive || !first) return;
      const lastCut = first.last_settlement_window_end;
      const defaultStart = first.window_start;
      const endIso = first.window_end;
      const endInput = utcIsoToInput(endIso);
      setWEnd(endInput);
      if (lastCut && new Date(lastCut) < new Date(defaultStart)) {
        const startInput = utcIsoToInput(lastCut);
        setWStart(startInput);
        const full = await fetchPreview(lastCut, endIso);
        if (alive && full) {
          setData(full);
          setQueried({ start: inputToUtcIso(startInput), end: inputToUtcIso(endInput), full: false });
        }
      } else {
        const startInput = utcIsoToInput(defaultStart);
        setWStart(startInput);
        setData(first);
        setQueried({ start: inputToUtcIso(startInput), end: inputToUtcIso(endInput), full: false });
      }
    })();
    return () => { alive = false; };
  }, [merchant.merchant_id, fetchPreview]);

  const recalc = async () => {
    setExecResult(null);
    if (fullBalance) {
      const d = await fetchPreview(null, null, true);
      if (d) { setData(d); setQueried({ start: null, end: null, full: true }); }
      return;
    }
    const startIso = inputToUtcIso(wStart), endIso = inputToUtcIso(wEnd);
    const d = await fetchPreview(startIso, endIso, false);
    if (d) {
      setData(d);
      setQueried({ start: startIso, end: endIso, full: false });
    }
  };

  // Al alternar "barrer saldo completo" re-simula automáticamente.
  const toggleFullBalance = async (checked) => {
    setFullBalance(checked);
    setExecResult(null);
    if (checked) {
      const d = await fetchPreview(null, null, true);
      if (d) { setData(d); setQueried({ start: null, end: null, full: true }); }
    } else {
      const startIso = inputToUtcIso(wStart), endIso = inputToUtcIso(wEnd);
      const d = await fetchPreview(startIso, endIso, false);
      if (d) { setData(d); setQueried({ start: startIso, end: endIso, full: false }); }
    }
  };

  // La ventana ejecutada debe ser EXACTAMENTE la simulada: bloquea el botón
  // si el usuario cambió las fechas sin volver a recalcular. Se compara contra
  // la ventana CONSULTADA (el input), no contra data.window_start, porque el
  // backend puede recortar el inicio al último barrido (con segundos) y ese
  // valor nunca coincidiría con un datetime-local (precisión de minutos).
  const inSync = !!(
    data && (
      (fullBalance && queried.full) ||
      (!fullBalance && !queried.full &&
        inputToUtcIso(wStart) === queried.start &&
        inputToUtcIso(wEnd) === queried.end)
    )
  );

  const executeSettlement = async () => {
    if (!data || !inSync) return;
    const msg = `⚠️ EJECUTAR CORTE REAL (mueve dinero)\n\n`
      + `Comercio: ${data.company_name}\n`
      + (fullBalance
          ? `Modo: BARRIDO DE SALDO COMPLETO (todo el backlog)\nSaldo del ledger: ${fmtVES(data.current_ledger_balance)}\n`
          : `Ventana: ${fmtDate(data.window_start)} → ${fmtDate(data.window_end)}\n`)
      + (data.flow_direction === 'merchant_to_caura'
          ? `Neto${fullBalance ? '' : ' de la ventana'}: ${fmtVES(data.net_income)}\n`
            + `Comisión CauraPay (${data.rate_pct}%): ${fmtVES(data.commission_amount)}\n`
            + `Barrido al comercio: ${fmtVES(data.sweep_estimate)}\n`
            + `Queda en ledger: ${fmtVES(data.ledger_after_estimate)}\n`
          : `Neto: ${fmtVES(data.net_income)}\n`
            + `Comisión CauraPay (${data.rate_pct}%): ${fmtVES(data.commission_amount)}\n`)
      + `\nSe ejecutará el CCE de comisión + el barrido del saldo. Esta acción NO se puede deshacer.\n\n¿Confirmas?`;
    if (!window.confirm(msg)) return;
    setExecuting(true); setExecResult(null);
    try {
      const body = fullBalance
        ? { merchant_id: merchant.merchant_id, full_balance: true }
        : { merchant_id: merchant.merchant_id, window_start: inputToUtcIso(wStart), window_end: inputToUtcIso(wEnd) };
      const res = await axios.post(
        `${API}/alliance-settlements/run-now`,
        body,
        { headers: headers() },
      );
      const result = res.data?.result || res.data;
      const success = result?.ok === true;
      setExecResult({
        ok: success,
        data: result,
        blocked: result?.blocked_unresolved === true || result?.blocked_sweep_in_progress === true,
        unknown: result?.bank_transfer_status === 'unknown',
        error: success ? null : (result?.error || result?.bank_transfer_error || 'El corte no se completó'),
      });
    } catch (e) {
      setExecResult({ ok: false, error: e.response?.data?.detail || 'Error al ejecutar el corte' });
    } finally {
      setExecuting(false);
    }
  };

  const spanHours = data ? (new Date(data.window_end) - new Date(data.window_start)) / 3.6e6 : 0;

  return (
    <Modal title={`Simulación de corte — ${merchant.email}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Desde (UTC)</label>
            <input type="datetime-local" value={wStart} onChange={(e) => setWStart(e.target.value)} disabled={fullBalance}
              data-testid="preview-window-start" className="w-full border rounded-lg px-2 py-1.5 text-xs disabled:bg-gray-100 disabled:text-gray-400" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Hasta (UTC)</label>
            <input type="datetime-local" value={wEnd} onChange={(e) => setWEnd(e.target.value)} disabled={fullBalance}
              data-testid="preview-window-end" className="w-full border rounded-lg px-2 py-1.5 text-xs disabled:bg-gray-100 disabled:text-gray-400" />
          </div>
        </div>
        <label className="flex items-start gap-2 bg-purple-50 border border-purple-200 rounded-lg p-2.5 cursor-pointer" data-testid="preview-full-balance-toggle">
          <input type="checkbox" checked={fullBalance} onChange={(e) => toggleFullBalance(e.target.checked)}
            className="mt-0.5" data-testid="preview-full-balance-checkbox" />
          <span className="text-[11px] text-purple-900">
            <strong>Barrer saldo completo (backlog)</strong> — ignora la ventana y liquida TODO el saldo del ledger: cobra la comisión de Caura ({data ? data.rate_pct : '—'}%) sobre el saldo actual y dispersa el resto, dejando el ledger en 0. Úsalo cuando el corte por ventana dejó pagos viejos atascados.
          </span>
        </label>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-400">Horas en UTC. 22:15 UTC = 6:15 p.m. Caracas.</p>
          <button onClick={recalc} disabled={loading} data-testid="preview-recalc-btn"
            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">
            {loading ? 'Calculando…' : 'Recalcular'}
          </button>
        </div>

        {loading && !data ? (
          <div className="py-6 text-center text-gray-400" data-testid="preview-loading">Calculando…</div>
        ) : err ? (
          <div className="py-4 text-center text-red-600 text-sm" data-testid="preview-error">{err}</div>
        ) : data && (
          <div className="space-y-3" data-testid="preview-content">
            {data.full_balance ? (
              <div className="bg-purple-50 border border-purple-300 rounded-lg p-2 text-[11px] text-purple-900" data-testid="preview-full-balance-mode">
                <strong>Barrido de saldo completo.</strong> Se liquida todo el saldo del ledger ({fmtVES(data.current_ledger_balance)}), sin importar la ventana. El ledger quedará en 0.
              </div>
            ) : (
              <p className="text-xs text-gray-500">Ventana: {fmtDate(data.window_start)} → {fmtDate(data.window_end)} · {data.orders_count} órden(es)</p>
            )}
            {!data.full_balance && data.window_start_clamped_to_last_sweep && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-800" data-testid="preview-window-clamped">
                El inicio de la ventana se ajustó al último barrido ({fmtDate(data.window_start)}) para no recontar fees bancarios que ya salieron en el corte anterior. Así el ledger queda en 0 tras este corte.
              </div>
            )}
            {!data.full_balance && spanHours > 25 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-800" data-testid="preview-multiday">
                Ventana de {Math.round(spanHours / 24 * 10) / 10} día(s) — recuperando el período completo desde el último corte.
              </div>
            )}
            <div className="border rounded-lg divide-y">
              {data.full_balance ? (
                <PreviewRow label="Saldo del ledger (base comisión)" value={fmtVES(data.net_income)} bold />
              ) : (
                <>
                  <PreviewRow label="Ingresos brutos ventana (payment_in)" value={fmtVES(data.gross_income)} />
                  <PreviewRow label="Comisiones banco (−)" value={fmtVES(data.bank_commissions)} negative />
                  <PreviewRow label="Refunds (−)" value={fmtVES(data.refunds)} negative />
                  <PreviewRow label="Neto de la ventana" value={fmtVES(data.net_income)} bold />
                  <PreviewRow
                    label={data.commission_basis === 'window_gross'
                      ? 'Base comisión Caura (bruto − refunds)'
                      : 'Base comisión Caura (neto)'}
                    value={fmtVES(data.commission_base_amount ?? data.net_income)}
                    bold
                  />
                </>
              )}
              {data.flow_direction === 'merchant_to_caura' ? (
                <>
                  <PreviewRow label={`Comisión CauraPay (${data.rate_pct}% ${data.commission_basis === 'window_gross' ? 'del bruto' : 'del neto'})`} value={fmtVES(data.commission_amount)} bold highlight />
                  <PreviewRow label={data.full_balance ? 'Barrido al comercio (saldo − comisión)' : 'Barrido al comercio (neto − comisión)'} value={fmtVES(data.sweep_estimate)} bold />
                  <PreviewRow label="Saldo del ledger actual" value={fmtVES(data.current_ledger_balance)} />
                  <PreviewRow label={data.full_balance ? 'Queda en ledger' : 'Queda en ledger (próximo corte)'} value={fmtVES(data.ledger_after_estimate)} />
                </>
              ) : (
                <>
                  <PreviewRow label={`Comisión CauraPay (${data.rate_pct}% ${data.commission_basis === 'window_gross' ? 'del bruto' : 'del neto'})`} value={fmtVES(data.commission_amount)} bold highlight />
                  <PreviewRow label={data.full_balance ? 'Barrido al comercio (saldo − comisión)' : 'Saldo a dispersar (ledger − comisión)'} value={fmtVES(data.sweep_estimate)} bold />
                </>
              )}
            </div>
            {data.sweep_capped && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800" data-testid="preview-sweep-capped">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                El barrido se ajustó al saldo real del ledger ({fmtVES(data.sweep_estimate)}), menor que "neto − comisión". Ocurre cuando se debitaron fees bancarios después de cerrar la ventana. No es un error: se barre el saldo disponible.
              </div>
            )}
            {data.skipped_bank_fees?.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800" data-testid="preview-anomaly">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                {data.skipped_bank_fees.length} débito(s) grande(s) IGNORADO(s) por {fmtVES(data.skipped_bank_fees_total)} — no afectan el cálculo.
              </div>
            )}
            {data.bank_fee_reversal > 0 && (
              <p className="text-[11px] text-gray-500">Reversiones de fee aplicadas: {fmtVES(data.bank_fee_reversal)}</p>
            )}
            <p className="text-[11px] text-gray-400">{data.note}</p>

            {!execResult ? (
              <div className="pt-1 border-t space-y-2">
                {data.sweep_in_progress && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900" data-testid="preview-sweep-in-progress">
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                    <strong>Hay un barrido EN CURSO</strong> para este comercio (iniciado {fmtDate(data.sweep_started_at)}).
                    Espera a que termine antes de ejecutar otro corte: correrlo ahora barre dos veces el mismo dinero.
                  </div>
                )}
                {!inSync && (
                  <p className="text-[11px] text-amber-600" data-testid="preview-outofsync">
                    Cambiaste la ventana — pulsa "Recalcular" antes de ejecutar.
                  </p>
                )}
                <button onClick={executeSettlement} disabled={executing || !inSync || !!data.sweep_in_progress}
                  data-testid="preview-execute-btn"
                  className="w-full px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" /> {executing ? 'Ejecutando corte…' : data.sweep_in_progress ? 'Barrido en curso — espera' : 'Ejecutar este corte (mueve dinero)'}
                </button>
                <p className="text-[10px] text-gray-400 text-center">
                  Ejecuta el CCE de comisión + barrido usando exactamente la ventana simulada.
                  Al ejecutar se capturan las fees reales del día, así que el neto puede variar levemente.
                </p>
              </div>
            ) : execResult.ok ? (
              <div className="bg-green-50 border border-green-300 rounded-lg p-3 text-xs text-green-800 space-y-1" data-testid="preview-exec-success">
                <p className="font-semibold">✓ Corte ejecutado</p>
                <p>Estado banco: <span className="font-mono">{execResult.data?.bank_transfer_status || '—'}</span></p>
                <p>Comisión cobrada: {fmtVES(execResult.data?.commission_amount)}</p>
                {execResult.data?.bank_transfer_reference && (
                  <p>Ref. bancaria: <span className="font-mono">{execResult.data.bank_transfer_reference}</span></p>
                )}
                {execResult.data?.dispersion && (
                  <p>Barrido: {execResult.data.dispersion.ok ? `✓ ${fmtVES(execResult.data.dispersion.swept)}` : `pendiente (${execResult.data.dispersion.error || 'reintentar'})`}</p>
                )}
                {execResult.data?.bank_transfer_error && (
                  <p className="text-red-600">Error banco: {execResult.data.bank_transfer_error}</p>
                )}
                <p className="text-[10px] text-gray-500">Revisa el detalle completo en la pestaña Alianzas.</p>
              </div>
            ) : (execResult.blocked || execResult.unknown) ? (
              <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 text-xs text-orange-800 space-y-1" data-testid="preview-exec-blocked">
                <p className="font-semibold">⚠ Corte NO ejecutado — pendiente de verificación</p>
                <p>{execResult.error}</p>
                <p className="text-[10px] text-orange-700">
                  {execResult.unknown
                    ? 'El banco dio timeout: la comisión pudo haberse enviado. Verifica en el banco y resuelve el corte en la pestaña Alianzas antes de reintentar (evita doble cobro a Caura).'
                    : 'Resuelve el corte anterior pendiente en la pestaña Alianzas antes de correr uno nuevo.'}
                </p>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-xs text-red-700" data-testid="preview-exec-error">
                ✗ {execResult.error}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function RebuildModal({ onClose, onDone, setToast }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const runDry = useCallback(async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ledger/rebuild`, { dry_run: true }, { headers: headers() });
      setPreview(res.data.results);
    } catch (e) {
      setToast({ type: 'error', message: 'Error en dry-run' });
    } finally {
      setBusy(false);
    }
  }, [setToast]);
  useEffect(() => { runDry(); }, [runDry]);

  const write = async () => {
    if (!window.confirm('¿Confirmas ESCRIBIR la reconstrucción? Se borrarán los asientos previos (force).')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/ledger/rebuild`, { dry_run: false, force: true }, { headers: headers() });
      setToast({ type: 'success', message: 'Ledger reconstruido' });
      onDone();
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.detail || 'Error al reconstruir' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Reconstrucción histórica del ledger" onClose={onClose}>
      {!preview ? (
        <div className="text-center py-8 text-gray-400">Calculando dry-run…</div>
      ) : (
        <div className="space-y-4">
          {preview.map((r) => (
            <div key={r.merchant_id} className="border rounded-lg p-3 text-sm">
              <div className="flex justify-between font-semibold text-gray-800">
                <span>{r.email || r.merchant_id}</span>
                <span data-testid={`rebuild-balance-${r.merchant_id}`}>{r.ok ? fmtVES(r.final_balance) : '⚠️'}</span>
              </div>
              {r.ok ? (
                <>
                  <div className="text-gray-500 mt-1">{r.entries_count} asientos · créditos {fmtVES(r.total_credits)} · débitos {fmtVES(r.total_debits)}</div>
                  {(r.warnings || []).map((w, i) => (
                    <div key={i} className="text-amber-700 text-xs mt-1">⚠ {w}</div>
                  ))}
                </>
              ) : <div className="text-red-600 text-xs mt-1">{r.error}</div>}
            </div>
          ))}
          <button
            data-testid="rebuild-write-btn"
            disabled={busy}
            onClick={write}
            className="w-full bg-red-700 text-white rounded-lg py-2.5 font-semibold hover:bg-red-800 disabled:opacity-50"
          >
            {busy ? 'Procesando…' : 'Escribir reconstrucción (force)'}
          </button>
        </div>
      )}
    </Modal>
  );
}

export default function LedgerTab({ setToast }) {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statement, setStatement] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [range, setRange] = useState({ start: '', end: '' });
  const [exporting, setExporting] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [modal, setModal] = useState(null); // {type: 'disperse'|'adjust'|'rebuild', merchant}

  const exportCsv = async () => {
    if (!selected) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('entry_type', typeFilter);
      if (range.start) params.set('start', `${range.start}T00:00:00`);
      if (range.end) params.set('end', `${range.end}T23:59:59`);
      const qs = params.toString();
      const name = await downloadAuthedFile(
        `${API}/ledger/${selected.merchant_id}/export${qs ? `?${qs}` : ''}`,
        'estado_cuenta.csv',
      );
      setToast({ type: 'success', message: `Descargado: ${name}` });
    } catch (e) {
      setToast({ type: 'error', message: 'No se pudo exportar el estado de cuenta' });
    } finally {
      setExporting(false);
    }
  };

  const exportActivityReport = async () => {
    if (!selected) return;
    setReporting(true);
    try {
      const params = new URLSearchParams();
      if (range.start) params.set('start', `${range.start}T00:00:00-04:00`);
      if (range.end) params.set('end', `${range.end}T23:59:59-04:00`);
      const qs = params.toString();
      const name = await downloadAuthedFile(
        `${API}/ledger/${selected.merchant_id}/activity-report${qs ? `?${qs}` : ''}`,
        'reporte_comercio.csv',
      );
      setToast({ type: 'success', message: `Descargado: ${name}` });
    } catch (e) {
      setToast({ type: 'error', message: 'No se pudo generar el reporte' });
    } finally {
      setReporting(false);
    }
  };

  const loadBalances = useCallback(async () => {    setLoading(true);
    try {
      const res = await axios.get(`${API}/ledger/balances`, { headers: headers() });
      setBalances(res.data.balances || []);
    } catch (e) {
      setToast({ type: 'error', message: 'Error cargando saldos' });
    } finally {
      setLoading(false);
    }
  }, [setToast]);

  const loadStatement = useCallback(async (merchantId, type) => {
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (type) params.set('entry_type', type);
      const res = await axios.get(`${API}/ledger/${merchantId}/statement?${params}`, { headers: headers() });
      setStatement(res.data);
    } catch (e) {
      setToast({ type: 'error', message: 'Error cargando estado de cuenta' });
    }
  }, [setToast]);

  useEffect(() => { loadBalances(); }, [loadBalances]);
  useEffect(() => {
    if (selected) loadStatement(selected.merchant_id, typeFilter);
  }, [selected, typeFilter, loadStatement]);

  const refreshAll = () => {
    loadBalances();
    if (selected) loadStatement(selected.merchant_id, typeFilter);
  };

  if (selected) {
    return (
      <div className="space-y-5" data-testid="ledger-detail-view">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button
            data-testid="ledger-back-btn"
            onClick={() => { setSelected(null); setStatement(null); setTypeFilter(''); }}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" /> Volver a saldos
          </button>
          <div className="flex gap-2">
            <button
              data-testid="ledger-export-csv-btn"
              onClick={exportCsv}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-50"
            ><Download className="w-4 h-4" /> {exporting ? 'Generando…' : 'Exportar CSV'}</button>
            <button
              data-testid="ledger-activity-report-btn"
              onClick={exportActivityReport}
              disabled={reporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-50 disabled:opacity-50"
            ><Download className="w-4 h-4" /> {reporting ? 'Generando…' : 'Cobrado vs dispersado'}</button>
            <button
              data-testid="ledger-preview-btn"
              onClick={() => setModal({ type: 'preview', merchant: selected })}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50"
            ><BookOpen className="w-4 h-4" /> Simular corte</button>
            <button
              data-testid="ledger-disperse-btn"
              onClick={() => setModal({ type: 'disperse', merchant: { ...selected, balance: statement?.balance ?? selected.balance } })}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-purple-700 text-white rounded-lg hover:bg-purple-800"
            ><Send className="w-4 h-4" /> Dispersar</button>
            <button
              data-testid="ledger-adjust-btn"
              onClick={() => setModal({ type: 'adjust', merchant: selected })}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
            ><SlidersHorizontal className="w-4 h-4" /> Ajustar</button>
          </div>
        </div>
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-xl p-5 text-white flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm text-blue-200">{selected.email} · {selected.flow_direction === 'caura_to_merchant' ? 'Cobra en concentradora Caura' : 'Cuenta propia'}</p>
            <p className="text-3xl font-bold" data-testid="ledger-detail-balance">{statement ? fmtVES(statement.balance) : '…'}</p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <label className="text-xs text-blue-100">
              Desde
              <input
                type="date" data-testid="ledger-export-start"
                value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })}
                className="block border rounded-lg px-2 py-1.5 text-sm text-gray-800"
              />
            </label>
            <label className="text-xs text-blue-100">
              Hasta
              <input
                type="date" data-testid="ledger-export-end"
                value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })}
                className="block border rounded-lg px-2 py-1.5 text-sm text-gray-800"
              />
            </label>
            <select
              data-testid="ledger-detail-type-filter"
              value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm text-gray-800"
            >
              <option value="">Todos los tipos</option>
              {Object.entries(ENTRY_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <LedgerStatementTable entries={statement?.entries} />
        </div>
        {modal?.type === 'disperse' && (
          <DisperseModal merchant={modal.merchant} setToast={setToast} onClose={() => setModal(null)}
            onDone={() => { setModal(null); refreshAll(); }} />
        )}
        {modal?.type === 'adjust' && (
          <AdjustModal merchant={modal.merchant} setToast={setToast} onClose={() => setModal(null)}
            onDone={() => { setModal(null); refreshAll(); }} />
        )}
        {modal?.type === 'preview' && (
          <PreviewModal merchant={modal.merchant} onClose={() => setModal(null)} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ledger-balances-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-blue-700" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Saldos por Comercio</h2>
            <p className="text-sm text-gray-500">Ledger auditable: entra el pago, se restan fees, comisión y dispersiones</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="ledger-rebuild-btn"
            onClick={() => setModal({ type: 'rebuild' })}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
          ><Hammer className="w-4 h-4" /> Reconstruir histórico</button>
          <button
            data-testid="ledger-balances-refresh"
            onClick={loadBalances}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
          ><RefreshCw className="w-4 h-4" /> Actualizar</button>
        </div>
      </div>
      {loading ? (
        <div className="text-center py-10 text-gray-400">Cargando…</div>
      ) : (
        <>
        <OrphanCreditsPanel setToast={setToast} onReconciled={refreshAll} />
        <BankReconciliation />
        <DuplicatePaymentsCard setToast={setToast} />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {balances.map((b) => (
            <div key={b.merchant_id} className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid={`ledger-card-${b.merchant_id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{b.company_name || b.email}</p>
                  <p className="text-xs text-gray-500 truncate">{b.email}</p>
                </div>
                <Wallet className="w-5 h-5 text-blue-600 shrink-0" />
              </div>
              <p className="text-2xl font-bold mt-3 text-gray-900" data-testid={`ledger-balance-${b.merchant_id}`}>{fmtVES(b.balance)}</p>
              {b.last_settlement_anomalies && (
                <div
                  data-testid={`ledger-anomaly-badge-${b.merchant_id}`}
                  title={`El último corte ignoró ${b.last_settlement_anomalies.count} débito(s) grande(s) por ${fmtVES(b.last_settlement_anomalies.total)} (probables barridos/retiros mal capturados como fee bancario). No afectaron el cálculo.`}
                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-xs font-semibold"
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {b.last_settlement_anomalies.count} débito(s) descartado(s) · {fmtVES(b.last_settlement_anomalies.total)}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                <p>Créditos: {fmtVES(b.total_credits)} · Débitos: {fmtVES(b.total_debits)}</p>
                <p>Último asiento: {fmtDate(b.last_entry_at)}</p>
                <p>
                  {b.flow_direction === 'caura_to_merchant' ? 'Concentradora Caura' : 'Cuenta propia'} · {b.rate_pct}%
                  {b.settlement_paused && <span className="ml-1 text-amber-600 font-semibold">· PAUSADO</span>}
                </p>
              </div>
              <AutoSwitches merchant={b} setToast={setToast} onChanged={loadBalances} />
              <div className="flex gap-2 mt-4">
                <button
                  data-testid={`ledger-view-${b.merchant_id}`}
                  onClick={() => setSelected(b)}
                  className="flex-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                >Ver hoja</button>
                <button
                  data-testid={`ledger-quick-disperse-${b.merchant_id}`}
                  onClick={() => setModal({ type: 'disperse', merchant: b })}
                  disabled={b.balance <= 0}
                  className="flex-1 px-3 py-1.5 text-sm bg-purple-700 text-white rounded-lg hover:bg-purple-800 disabled:opacity-40"
                >Dispersar</button>
              </div>
            </div>
          ))}
          {balances.length === 0 && (
            <div className="col-span-full text-center py-10 text-gray-400">Sin comercios en modelo alianza</div>
          )}
        </div>
        </>
      )}
      {modal?.type === 'disperse' && (
        <DisperseModal merchant={modal.merchant} setToast={setToast} onClose={() => setModal(null)}
          onDone={() => { setModal(null); refreshAll(); }} />
      )}
      {modal?.type === 'rebuild' && (
        <RebuildModal setToast={setToast} onClose={() => setModal(null)}
          onDone={() => { setModal(null); refreshAll(); }} />
      )}
    </div>
  );
}
